import { z } from "zod"

import {
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const timestamp = z.string().datetime({ offset: false })
const base = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.number().int().nonnegative(),
}).strict()

export const taxableEventBasisV1Schema = z.enum([
  "service_completion",
  "invoice",
  "payment",
])
export const taxExemptionStatusV1Schema = z.enum(["taxable", "exempt", "unknown"])
export const taxTreatmentV1Schema = z.enum(["taxable", "exempt"])

export const transactionTaxRulePublishV1Schema = base.extend({
  ruleVersionId: entityIdSchema,
  ruleCode: z.string().regex(/^[A-Z0-9][A-Z0-9._-]{0,31}$/),
  ruleVersion: z.number().int().positive(),
  legalEntityId: legalEntityIdSchema,
  jurisdictionCode: z.string().trim().min(2).max(64),
  serviceCategory: z.string().trim().min(1).max(64),
  exemptionStatus: taxExemptionStatusV1Schema,
  effectiveFrom: timestamp,
  effectiveUntil: timestamp.nullable(),
  taxableEventBasis: taxableEventBasisV1Schema,
  treatment: taxTreatmentV1Schema,
  rateMicros: z.number().int().min(0).max(1_000_000),
  erpnextTaxTemplate: z.string().trim().min(1).max(140).nullable(),
  manualOverride: z.boolean(),
  overrideReason: z.string().trim().min(1).max(1_000).nullable(),
}).strict().superRefine((value, context) => {
  if (value.effectiveUntil !== null && value.effectiveUntil <= value.effectiveFrom) {
    context.addIssue({ code: "custom", message: "The effective window must be positive." })
  }
  if (value.treatment === "exempt" && value.rateMicros !== 0) {
    context.addIssue({ code: "custom", message: "Exempt treatment must have zero rate." })
  }
  if (value.manualOverride !== (value.overrideReason !== null)) {
    context.addIssue({ code: "custom", message: "Manual override requires exactly one reason." })
  }
})

export const transactionTaxQuoteV1Schema = base.extend({
  snapshotId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  billingProfileId: entityIdSchema,
  jurisdictionCode: z.string().trim().min(2).max(64),
  serviceCategory: z.string().trim().min(1).max(64),
  exemptionStatus: taxExemptionStatusV1Schema,
  taxableEventAt: timestamp,
  amountMinor: z.number().int().nonnegative(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  targetType: z.enum([
    "estimate_revision",
    "service_order",
    "change_order",
    "invoice",
  ]),
  targetId: entityIdSchema,
}).strict()

export const transactionTaxSnapshotV1Schema = z.object({
  snapshotId: entityIdSchema,
  status: z.enum(["resolved", "tax_review_required"]),
  ruleVersionId: entityIdSchema.nullable(),
  taxableEventBasis: taxableEventBasisV1Schema.nullable(),
  rateMicros: z.number().int().nonnegative().nullable(),
  taxAmountMinor: z.number().int().nonnegative().nullable(),
  inputFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  reviewReason: z.enum(["missing_rule", "ambiguous_rule", "manual_override"]).nullable(),
}).strict()

export const transactionTaxReviewResolveV1Schema = base.extend({
  reviewId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  decision: z.enum(["approve", "reject"]),
  selectedRuleVersionId: entityIdSchema.nullable(),
  reason: z.string().trim().min(1).max(1_000),
}).strict().superRefine((value, context) => {
  if ((value.decision === "approve") !== (value.selectedRuleVersionId !== null)) {
    context.addIssue({
      code: "custom",
      message: "Approval requires exactly one selected rule version.",
    })
  }
})

export const transactionTaxReviewQueryV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  legalEntityId: legalEntityIdSchema,
  state: z.enum(["open", "approved", "rejected"]),
  cursor: z.string().max(256).nullable(),
  limit: z.number().int().min(1).max(100),
}).strict()

export const transactionTaxReviewResultV1Schema = z.object({
  entityType: z.literal("transaction_tax_review"),
  entityId: entityIdSchema,
  state: z.enum(["approved", "rejected"]),
  version: z.literal(2),
  selectedRuleVersionId: entityIdSchema.nullable(),
  inputFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  decisionReason: z.string().trim().min(1).max(1_000),
  resolvedAt: timestamp,
}).strict()

export type TransactionTaxRulePublishV1 = z.infer<
  typeof transactionTaxRulePublishV1Schema
>
export type TransactionTaxQuoteV1 = z.infer<typeof transactionTaxQuoteV1Schema>
export type TransactionTaxSnapshotV1 = z.infer<typeof transactionTaxSnapshotV1Schema>
export type TransactionTaxReviewResolveV1 = z.infer<
  typeof transactionTaxReviewResolveV1Schema
>
